import React, { useState, useEffect, useMemo } from 'react';
import { Loader2, LayoutGrid, X } from 'lucide-react';

// Contextos e Provedores
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AnonymizeProvider } from './contexts/AnonymizeContext';

// Serviços e Configurações
import { useFirestoreData } from './services/firestore';
import { MODULES, ICON_MAP } from './config/constants';
import { getDynamicIcon } from './utils';

// Componentes Estruturais
import Sidebar from './app/Sidebar';
import BarraSuperior from './app/BarraSuperior';
import CentralAjuda from './components/CentralAjuda';
import { Database, Moon, Sun, Leaf } from 'lucide-react';

// Componentes de Módulos e Telas
import LoginScreen from './modules/login/LoginScreen';
import RegistrationScreen from './modules/login/RegistrationScreen';
import PendingScreen from './modules/login/PendingScreen';
import AccessBlockedScreen from './modules/login/AccessBlockedScreen';
import Dashboard from './modules/dashboard/Dashboard';
import FavoritesScreen from './modules/favorites/FavoritesScreen';
import AdmModule from './modules/adm/AdmModule';

function HubApp() {
    const { theme, setTheme, isDarkMode, setIsDarkMode } = useTheme();
    const { 
        currentUser, 
        authReady, 
        loginWithGoogle, 
        loginWithGodMode, 
        logout, 
        submitRegistration 
    } = useAuth();

    // Estados locais do Hub
    const [activeModule, setActiveModule] = useState(null);
    const [draggedItem, setDraggedItem] = useState(null);
    const [openTabs, setOpenTabs] = useState([]);
    const [forceSidebarOpen, setForceSidebarOpen] = useState(true); 
    const [sidebarHidden, setSidebarHidden] = useState(() => {
        return localStorage.getItem('hub_sidebar_hidden') === 'true';
    });
    const handleSetSidebarHidden = (val) => {
        setSidebarHidden(prev => {
            const next = typeof val === 'function' ? val(prev) : val;
            localStorage.setItem('hub_sidebar_hidden', String(next));
            return next;
        });
    };
    const [hideMenuNumbers, setHideMenuNumbers] = useState(() => {
        return localStorage.getItem('apex_hide_menu_numbers') === 'true';
    });
    const toggleHideMenuNumbers = () => {
        setHideMenuNumbers(prev => {
            const next = !prev;
            localStorage.setItem('apex_hide_menu_numbers', String(next));
            window.dispatchEvent(new CustomEvent('apex_hide_menu_numbers_changed', { detail: next }));
            window.dispatchEvent(new Event('storage'));
            return next;
        });
    };
    const [isDefaultFavorites, setIsDefaultFavorites] = useState(false);
    const [expandedSidebar, setExpandedSidebar] = useState({});

    // Interface Zoom scale logic (A-, Restore, A+)
    const [interfaceZoom, setInterfaceZoom] = useState(() => {
        try {
            const salvo = Number(localStorage.getItem('facil_interface_zoom'));
            return Number.isFinite(salvo) && salvo >= 70 && salvo <= 130 ? salvo : 100;
        } catch {
            return 100;
        }
    });

    useEffect(() => {
        const root = document.documentElement;
        const tamanhoAnterior = root.style.fontSize;
        const transicaoAnterior = root.style.transition;

        root.style.transition = 'font-size 160ms ease';
        root.style.fontSize = `${interfaceZoom}%`;
        root.style.setProperty('--facil-interface-scale', String(interfaceZoom / 100));

        localStorage.setItem('facil_interface_zoom', String(interfaceZoom));

        return () => {
            root.style.fontSize = tamanhoAnterior;
            root.style.transition = transicaoAnterior;
            root.style.removeProperty('--facil-interface-scale');
        };
    }, [interfaceZoom]);

    const reduzirZoomInterface = () => setInterfaceZoom(prev => Math.max(70, prev - 10));
    const aumentarZoomInterface = () => setInterfaceZoom(prev => Math.min(130, prev + 10));
    const restaurarZoomInterface = () => setInterfaceZoom(100);

    // Cache clean logic
    const CATEGORIAS_CACHE = [
        {
            id: 'icms_ipi',
            label: 'ICMS | IPI',
            desc: 'SPEDs, apurações resumidas e confronto contábil. As configurações do Firebase são preservadas.',
            localStorageKeys: ['cache_icms_ipi', 'cache_sped_fiscal']
        },
        {
            id: 'lancamentos',
            label: 'Lançamentos',
            desc: 'Arquivos e movimentos importados localmente. Configurações permanentes são preservadas.',
            localStorageKeys: ['cache_lancamentos', 'cache_extratos']
        },
        {
            id: 'ecd_ecf',
            label: 'ECD | ECF',
            desc: 'Dados importados e caches de processamento local. Modelos/configurações não são excluídos.',
            localStorageKeys: ['cache_ecd_ecf', 'cache_ecd_data']
        },
        {
            id: 'conciliador',
            label: 'Conciliador',
            desc: 'Movimentos e resultados locais da conciliação. Configurações conciliador_cfg permanecem no Firebase.',
            localStorageKeys: ['cache_conciliador', 'cache_conciliador_cfg']
        },
        {
            id: 'tributos',
            label: 'Tributos',
            desc: 'Resultados e dados locais de consultas/importações. Cadastros e configurações são preservados.',
            localStorageKeys: ['cache_tributos', 'cache_guias']
        }
    ];

    const [modalLimpezaCacheAberto, setModalLimpezaCacheAberto] = useState(false);
    const [categoriasCacheSelecionadas, setCategoriasCacheSelecionadas] = useState(new Set());
    const [limpandoCacheCategorias, setLimpandoCacheCategorias] = useState(false);

    const alternarCategoriaCache = (id) => {
        setCategoriasCacheSelecionadas(prev => {
            const novo = new Set(prev);
            if (novo.has(id)) novo.delete(id);
            else novo.add(id);
            return novo;
        });
    };

    const confirmarLimpezaCache = async () => {
        setLimpandoCacheCategorias(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 800));
            CATEGORIAS_CACHE
                .filter(item => categoriasCacheSelecionadas.has(item.id))
                .forEach(item => {
                    item.localStorageKeys.forEach(key => {
                        localStorage.removeItem(key);
                    });
                });
            setModalLimpezaCacheAberto(false);
            setCategoriasCacheSelecionadas(new Set());
        } finally {
            setLimpandoCacheCategorias(false);
        }
    };

    // Central de Ajuda toggle
    const abrirCentralAjuda = () => {
        const id = 'ajuda-melhorias';
        const helpModule = {
            id,
            title: 'Ajuda e Melhorias',
            label: 'Ajuda e Melhorias',
            tabColor: '#0891b2',
            color: 'from-cyan-600 to-teal-500'
        };
        handleOpenModule(helpModule);
    };

    // Theme label mappings for BarraSuperior
    const proximoTema = () => {
        const ordemTemas = ['light', 'dark', 'acad'];
        const indice = ordemTemas.indexOf(theme);
        setTheme(ordemTemas[(indice + 1) % ordemTemas.length]);
    };

    const rotuloTema = theme === 'light' ? 'Claro' : theme === 'acad' ? 'Verde Acad' : 'Escuro';
    const IconeTema = theme === 'light' ? Sun : theme === 'acad' ? Leaf : Moon;

    // Notifications topo states and Firestore loader
    const [notificacoesTopoAberto, setNotificacoesTopoAberto] = useState(false);
    const { data: dbNotifications, saveItem: saveNotification } = useFirestoreData('notificacoes', authReady);
    const { data: hubSettings = [], saveItem: saveSetting } = useFirestoreData('hub_settings', authReady);

    // Local read status for mock or fallback notifications
    const [notificacoesLidasLocais, setNotificacoesLidasLocais] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('facil_notificacoes_lidas_v1')) || [];
        } catch {
            return [];
        }
    });

    const allNotifications = useMemo(() => {
        if (dbNotifications && dbNotifications.length > 0) return dbNotifications;
        // Mock fallback to display notifications in the badge for verification
        return [
            { id: 'notif-1', tipo: 'orientacao', titulo: 'Orientação Contábil', mensagem: 'Regras de encerramento do trimestre atualizadas.', destinatarioEmail: currentUser?.email || '', lidaPor: [] },
            { id: 'notif-2', tipo: 'sugestao', titulo: 'Sugestão de melhoria pendente', mensagem: 'Envie sugestões de melhoria clicando na Central de Ajuda.', destinatarioEmail: currentUser?.email || 'admins', lidaPor: [] }
        ];
    }, [dbNotifications, currentUser]);

    const userNotifications = useMemo(() => {
        if (!currentUser?.email) return [];
        const emailAtual = currentUser.email.trim().toLowerCase();
        
        return allNotifications.filter(item => {
            if (notificacoesLidasLocais.includes(item.id)) return false;

            const lidaPor = (item.lidaPor || [])
                .map(email => String(email).trim().toLowerCase());

            if (lidaPor.includes(emailAtual)) return false;

            const dest = String(item.destinatarioEmail || '').trim().toLowerCase();
            const isAdmin = currentUser.role === 'admin' || currentUser.role === 'Gestor';
            
            return dest === emailAtual || dest === 'admins' || isAdmin;
        });
    }, [allNotifications, currentUser, notificacoesLidasLocais]);

    const orientacoesCount = useMemo(() => userNotifications.filter(n => n.tipo === 'orientacao').length, [userNotifications]);
    const sugestoesCount = useMemo(() => userNotifications.filter(n => n.tipo === 'sugestao').length, [userNotifications]);

    const abrirNotificacao = async (item) => {
        const emailAtual = currentUser?.email ? currentUser.email.trim().toLowerCase() : 'usuario';
        
        // Save local read state
        const novasLidas = Array.from(new Set([...notificacoesLidasLocais, item.id]));
        setNotificacoesLidasLocais(novasLidas);
        localStorage.setItem('facil_notificacoes_lidas_v1', JSON.stringify(novasLidas));

        if (dbNotifications.some(n => n.id === item.id)) {
            const updatedLidaPor = [...(item.lidaPor || []), emailAtual];
            await saveNotification({ ...item, lidaPor: updatedLidaPor });
        }

        if (item.tipo === 'sugestao' || item.id === 'notif-2' || item.titulo?.toLowerCase().includes('sugestão')) {
            abrirCentralAjuda();
        } else if (item.tipo === 'orientacao' || item.id === 'notif-1' || item.titulo?.toLowerCase().includes('orientação')) {
            const modOrientacoes = displayModules.find(m => m.id === 'orientacoes' || m.id === '08');
            if (modOrientacoes) handleOpenModule(modOrientacoes);
            else abrirCentralAjuda();
        } else if (item.moduleId) {
            const mod = displayModules.find(m => m.id === item.moduleId);
            if (mod) handleOpenModule(mod);
        }
        setNotificacoesTopoAberto(false);
    };

    // User initials helper
    const obterIniciais = (usuario) => {
        if (!usuario) return 'US';
        const nome = usuario.name || usuario.displayName || usuario.email || 'Usuário';
        const partes = nome.split(' ').filter(Boolean);
        if (partes.length >= 2) {
            return (partes[0][0] + partes[1][0]).toUpperCase();
        }
        return nome.substring(0, 2).toUpperCase();
    };

    // ESTADOS DE ZOOM INDEPENDENTES (1 a 10)
    const [homeZoom, setHomeZoom] = useState(() => Number(localStorage.getItem('hubHomeZoom')) || 5);
    const [favZoom, setFavZoom] = useState(() => Number(localStorage.getItem('hubFavZoom')) || 5);

    const handleHomeZoomChange = (e) => {
        const val = Number(e.target.value);
        setHomeZoom(val);
        localStorage.setItem('hubHomeZoom', val);
    };

    const handleFavZoomChange = (e) => {
        const val = Number(e.target.value);
        setFavZoom(val);
        localStorage.setItem('hubFavZoom', val);
    };

    // Buscar configurações de Logomarca no banco (com suporte a documentos dedicados e legado)
    const logosDoc = (hubSettings || []).find(s => s.id === 'logos') || {};
    const logoLightDoc = (hubSettings || []).find(s => s.id === 'logo_sidebar_light');
    const logoDarkDoc = (hubSettings || []).find(s => s.id === 'logo_sidebar_dark');
    const logoAcadDoc = (hubSettings || []).find(s => s.id === 'logo_sidebar_acad');
    const logoLoginDoc = (hubSettings || []).find(s => s.id === 'logo_login');
    const logoSidebarDoc = (hubSettings || []).find(s => s.id === 'logo_sidebar');

    const logos = {
        ...logosDoc,
        sidebar_light: logoLightDoc?.value !== undefined ? logoLightDoc.value : (logosDoc.sidebar_light || null),
        sidebar_dark: logoDarkDoc?.value !== undefined ? logoDarkDoc.value : (logosDoc.sidebar_dark || null),
        sidebar_acad: logoAcadDoc?.value !== undefined ? logoAcadDoc.value : (logosDoc.sidebar_acad || null),
        sidebar: logoSidebarDoc?.value !== undefined ? logoSidebarDoc.value : (logosDoc.sidebar || null),
        login: logoLoginDoc?.value !== undefined ? (logoLoginDoc.value || "/LogoApEx2.0.png") : (logosDoc.login || "/LogoApEx2.0.png")
    };
    const loginLogo = logos.login || "/LogoApEx2.0.png";
    
    // Logo do cabeçalho selecionada de acordo com o modo de exibição (claro, escuro ou verde)
    const sidebarLogo = (
        theme === 'light' ? logos.sidebar_light :
        theme === 'acad' ? logos.sidebar_acad :
        logos.sidebar_dark
    ) || logos.sidebar || null;

    // 1. Carregar as abas salvas ao fazer Login ou F5
    useEffect(() => {
        if (currentUser?.id) {
            try {
                const savedTabs = JSON.parse(localStorage.getItem(`apex_tabs_${currentUser.id}`)) || [];
                const defaultScrn = localStorage.getItem(`apex_default_screen_${currentUser.id}`);
                
                setIsDefaultFavorites(defaultScrn === 'favorites');
                setOpenTabs(savedTabs);
                setActiveModule(defaultScrn === 'favorites' ? 'favorites' : null);
            } catch(e) { 
                console.error("Erro ao carregar cache de abas:", e); 
            }
        } else {
            setOpenTabs([]);
            setActiveModule(null);
            setIsDefaultFavorites(false);
        }
    }, [currentUser?.id]);

    // 2. Salvar automaticamente as abas no navegador sempre que mudarem
    useEffect(() => {
        if (currentUser?.id) {
            const sanitize = (mod) => {
                if (!mod) return null;
                const { icon, ...rest } = mod; // Remove componente de ícone para não falhar a serialização JSON
                return rest;
            };

            localStorage.setItem(`apex_tabs_${currentUser.id}`, JSON.stringify(openTabs.map(sanitize)));
            if (activeModule) localStorage.setItem(`apex_active_${currentUser.id}`, JSON.stringify(sanitize(activeModule)));
            else localStorage.removeItem(`apex_active_${currentUser.id}`);
        }
    }, [openTabs, activeModule, currentUser?.id]);

    // Grava os favoritos ocultados no navegador para não sumirem ao dar F5
    const [hubHiddenFavorites, setHubHiddenFavorites] = useState(() => {
        try { return JSON.parse(localStorage.getItem('hubHiddenFavs')) || []; } 
        catch { return []; }
    });
    
    // Controla se o usuário clicou no "olho" para ver os arquivados
    const [showHiddenFavorites, setShowHiddenFavorites] = useState(false);

    const isCollapsed = !forceSidebarOpen;
    
    const { data: dbModules, saveItem: saveModule } = useFirestoreData('hub_modules', authReady);
    const { data: allFavorites, saveItem: saveFavorite, deleteItem: deleteFavorite } = useFirestoreData('user_favorites', authReady);
    
    const userFavorites = allFavorites.filter(fav => fav.userId === currentUser?.id);

    const remoteFavorites = useMemo(() => {
        if (!currentUser) return [];
        return currentUser.favorites || currentUser.preferences?.favorites || []; 
    }, [currentUser]);

    const handleToggleFavorite = (e, mod) => {
        e.stopPropagation();
        const existingFav = userFavorites.find(f => f.moduleId === mod.id);
        if (existingFav) {
            deleteFavorite(existingFav.id);
        } else {
            saveFavorite({
                id: `${currentUser.id}_${mod.id}`,
                userId: currentUser.id,
                moduleId: mod.id,
                title: mod.title,
                color: mod.color || 'from-blue-600 to-cyan-500',
                iconName: mod.iconName || mod.icon?.displayName || 'LayoutGrid',
                customIconUrl: mod.customIconUrl || '',
                size: mod.size || 'normal',
                url: mod.url || '',
                isInternal: mod.isInternal || false,
                sourceApp: 'Hub Corporativo'
            });
        }
    };

    const handleHideFavoriteFromHub = (e, moduleId) => {
        e.stopPropagation();
        setHubHiddenFavorites(prev => {
            const newHidden = prev.includes(moduleId) ? prev.filter(id => id !== moduleId) : [...prev, moduleId];
            localStorage.setItem('hubHiddenFavs', JSON.stringify(newHidden));
            return newHidden;
        });
    };

    const displayModules = useMemo(() => {
        const dbMap = new Map(dbModules.map(m => [m.id, m]));
        const merged = [];
        
        MODULES.forEach(m => {
            if (dbMap.has(m.id)) {
                const dbMod = dbMap.get(m.id);
                if (!dbMod._deleted) {
                    merged.push({ 
                        ...m, 
                        ...dbMod, 
                        icon: ICON_MAP[dbMod.iconName] || m.icon || LayoutGrid 
                    });
                }
                dbMap.delete(m.id);
            } else {
                merged.push({ ...m, icon: m.icon || LayoutGrid });
            }
        });
        
        dbMap.forEach(m => {
            if (!m._deleted && !m.isRemote && !m.sourceApp && m.title) {
                merged.push({ 
                    ...m, 
                    icon: ICON_MAP[m.iconName] || LayoutGrid 
                });
            }
        });
        
        const hasDeptSubPermission = (modItem) => {
            if (currentUser?.role === 'admin' || currentUser?.role === 'ADM' || currentUser?.role === 'SuperAdm') return true;
            const subPermKey = `access_mod_${String(modItem.id).replace(/\./g, '_')}`;
            const subPermKeyRaw = `access_mod_${modItem.id}`;
            const appViewKey = modItem.title ? `${String(modItem.title).trim().toLowerCase()}_app_view` : '';
            if (currentUser?.permissions?.[subPermKey] !== undefined) {
                return currentUser.permissions[subPermKey] === true;
            }
            if (currentUser?.permissions?.[subPermKeyRaw] !== undefined) {
                return currentUser.permissions[subPermKeyRaw] === true;
            }
            if (appViewKey && currentUser?.permissions?.[appViewKey] !== undefined) {
                return currentUser.permissions[appViewKey] === true;
            }
            return false;
        };

        let finalMerged = merged.filter(m => {
            if (m.id === '10') return currentUser?.permissions?.['access_proj_av_desempenho'] === true;
            if (m.id === '99' || m.id === '60') {
                return currentUser?.permissions?.['access_mod_99'] === true || currentUser?.permissions?.['access_mod_60'] === true || currentUser?.role === 'admin' || currentUser?.role === 'ADM';
            }

            // Módulo 05 (Deptos): visível se ao menos um departamento independente (05.xx) estiver habilitado
            if (m.id === '05') {
                if (currentUser?.role === 'admin' || currentUser?.role === 'ADM' || currentUser?.role === 'SuperAdm') return true;
                const deptChildren = merged.filter(child => child.parentId === '05');
                if (deptChildren.some(child => hasDeptSubPermission(child))) return true;
                return currentUser?.permissions?.['access_mod_05'] === true;
            }

            // Subitens de Departamentos (Módulo 05) são tratados como módulos independentes
            if (m.parentId === '05') {
                return hasDeptSubPermission(m);
            }
            
            // Demais subitens herdam a permissão do módulo pai correspondente
            if (m.parentId) {
                const parentPermKey = `access_mod_${m.parentId}`;
                const hasParentPerm = currentUser?.permissions?.[parentPermKey] === true || currentUser?.role === 'admin' || currentUser?.role === 'ADM';
                if (!hasParentPerm) return false;

                if (currentUser?.role !== 'admin' && currentUser?.role !== 'ADM') {
                    const subPermKey = `access_mod_${String(m.id).replace('.', '_')}`;
                    const subPermKeyRaw = `access_mod_${m.id}`;
                    if (currentUser?.permissions?.[subPermKey] !== undefined) {
                        return currentUser.permissions[subPermKey] === true;
                    }
                    if (currentUser?.permissions?.[subPermKeyRaw] !== undefined) {
                        return currentUser.permissions[subPermKeyRaw] === true;
                    }
                }
                return true;
            }

            const isOriginalModule = MODULES.some(orig => orig.id === m.id);
            if (isOriginalModule) {
                const permKey = `access_mod_${String(m.id).replace('.', '_')}`;
                const permKeyRaw = `access_mod_${m.id}`;
                return currentUser?.permissions?.[permKey] === true || currentUser?.permissions?.[permKeyRaw] === true || currentUser?.role === 'admin' || currentUser?.role === 'ADM';
            }
            return true;
        });
        
        return finalMerged.sort((a,b) => {
            if (a.order !== undefined && b.order !== undefined && a.order !== b.order) return a.order - b.order;
            if (a.order !== undefined && b.order === undefined) return -1;
            if (b.order !== undefined && a.order === undefined) return 1;
            return String(a.id || '').localeCompare(String(b.id || ''), undefined, { numeric: true, sensitivity: 'base' });
        });
    }, [dbModules, currentUser]);

    const displayFavorites = useMemo(() => {
        const remoteProcessed = remoteFavorites.map(item => {
            if (!item || typeof item !== 'object') return null;
            let rawName = item.label || '';
            if (!rawName && item.fullPath) {
                const parts = item.fullPath.split('>');
                rawName = parts[parts.length - 1].trim();
            }
            if (!rawName) rawName = String(item.targetSys || item.id || 'Sem Nome');

            const formattedTitle = rawName.charAt(0).toUpperCase() + rawName.slice(1);
            const baseId = item.id || item.targetSys;
            const hubOverride = displayModules.find(m => m.id === baseId);
            const favOverride = allFavorites.find(f => f.userId === currentUser?.id && f.moduleId === baseId);

            return {
                id: baseId,
                title: formattedTitle,
                color: hubOverride?.color || item.color || "from-slate-500 to-slate-600",
                icon: ICON_MAP[item.iconName] || getDynamicIcon(formattedTitle),
                url: item.url || "https://apex-grupoacadcontabilidade.web.app/",
                size: favOverride?.size || hubOverride?.size || item.size || '1x1',
                order: favOverride?.order || item.order,
                isRemote: true
            };
        }).filter(Boolean);

        const myHubFavs = allFavorites.filter(f => f.userId === currentUser?.id);
        const hubProcessed = myHubFavs.map(fav => {
            const found = displayModules.find(m => m.id === fav.moduleId);
            return found ? { ...found, size: fav.size || found.size || '1x1', isRemote: false, order: fav.order } : null;
        }).filter(Boolean);

        const combined = [...remoteProcessed];
        hubProcessed.forEach(hubFav => {
            if (!combined.find(c => c.id === hubFav.id)) combined.push(hubFav);
        });

        const finalFavs = combined.filter(mod => mod && (showHiddenFavorites || !hubHiddenFavorites.includes(mod.id)));
        return finalFavs.sort((a,b) => {
            if (a.order !== undefined && b.order !== undefined) return a.order - b.order;
            if (a.order !== undefined) return -1;
            if (b.order !== undefined) return 1;
            return String(a.id || '').localeCompare(String(b.id || ''));
        });
    }, [displayModules, remoteFavorites, allFavorites, currentUser, hubHiddenFavorites, showHiddenFavorites]);

    const handleCycleCardSize = (e, mod, isFavoriteScreen) => {
        e.stopPropagation();
        const sizes = ['1x1', '2x1', '2x2', '1x2']; 
        
        let currentSize = mod.size || '1x1';
        if (currentSize === 'normal') currentSize = '1x1';
        if (currentSize === 'wide') currentSize = '2x1';
        if (currentSize === 'large') currentSize = '2x2';
        if (currentSize === 'tall') currentSize = '1x2';

        let nextIndex = sizes.indexOf(currentSize) + 1;
        if (nextIndex >= sizes.length) nextIndex = 0;
        const nextSize = sizes[nextIndex];

        if (isFavoriteScreen) {
            const existingFav = allFavorites.find(f => f.userId === currentUser?.id && f.moduleId === mod.id);
            if (existingFav) {
                saveFavorite({ ...existingFav, size: nextSize });
            } else {
                saveFavorite({
                    id: `${currentUser.id}_${mod.id}`,
                    userId: currentUser.id,
                    moduleId: mod.id,
                    title: mod.title,
                    color: mod.color || 'from-slate-500 to-slate-600',
                    size: nextSize
                });
            }
        } else {
            saveModule({ ...mod, size: nextSize });
        }
    };

    const handleDrop = (e, targetMod, isFavoriteScreen) => {
        e.preventDefault();
        if (!draggedItem || draggedItem.id === targetMod.id) return;

        const list = isFavoriteScreen ? displayFavorites : displayModules.filter(m => m.moduleType !== 'externo' && !m.parentId);
        const sourceIndex = list.findIndex(m => m.id === draggedItem.id);
        const targetIndex = list.findIndex(m => m.id === targetMod.id);

        const newList = [...list];
        const [removed] = newList.splice(sourceIndex, 1);
        newList.splice(targetIndex, 0, removed);

        newList.forEach((item, index) => {
            const newOrder = index + 1;
            if (item.order !== newOrder) {
                if (isFavoriteScreen) {
                    const fav = allFavorites.find(f => f.userId === currentUser?.id && f.moduleId === item.id);
                    if (fav) {
                        saveFavorite({...fav, order: newOrder});
                    } else {
                        saveFavorite({ id: `${currentUser?.id}_${item.id}`, userId: currentUser?.id, moduleId: item.id, title: item.title, order: newOrder });
                    }
                } else {
                    saveModule({...item, order: newOrder});
                }
            }
        });
        setDraggedItem(null);
    };

    const handleOpenModule = (mod) => {
        if (mod !== 'favorites' && mod !== null) {
            const hasChildren = displayModules.some(child => child.parentId === mod.id);
            if ((!mod.url || mod.openMode === 'none') && hasChildren) {
                setForceSidebarOpen(true);
                setExpandedSidebar(prev => ({...prev, [mod.id]: true}));
                return;
            }

            if ((mod.openMode === 'newTab' || mod.openInNewTab) && mod.url) {
                window.open(mod.url, '_blank');
                return;
            }

            setOpenTabs(prev => {
                if (!prev.find(t => t.id === mod.id)) return [...prev, mod];
                return prev;
            });
        }
        setActiveModule(mod);
    };

    const handleCloseTab = (e, modId) => {
        e.stopPropagation();
        setOpenTabs(prev => {
            const newTabs = prev.filter(t => t.id !== modId);
            if (activeModule?.id === modId) {
                if (newTabs.length > 0) setActiveModule(newTabs[newTabs.length - 1]);
                else setActiveModule(null);
            }
            return newTabs;
        });
    };

    // TELAS DE CARREGAMENTO E BLOQUEIO DE ACESSO
    if (!authReady) {
        return (
            <div className="h-screen w-full flex items-center justify-center bg-slate-900 text-white font-bold">
                <Loader2 className="animate-spin mr-2"/> Iniciando Ecossistema...
            </div>
        );
    }

    if (!currentUser) {
        return (
            <LoginScreen 
                onGoogleLogin={loginWithGoogle} 
                onGodModeLogin={loginWithGodMode} 
                isDarkMode={isDarkMode} 
                loginLogo={loginLogo} 
            />
        );
    }

    if (currentUser.needsRegistration) {
        return (
            <RegistrationScreen 
                user={currentUser} 
                onSubmit={submitRegistration} 
                onCancel={logout} 
            />
        );
    }

    if (currentUser.role === 'Solicitante' || currentUser.status === 'pendente') {
        return <PendingScreen onCancel={logout} />;
    }

    if (!currentUser?.permissions?.access_hub_basic) {
        return <AccessBlockedScreen onLogout={logout} />;
    }

    return (
        <div className={`flex h-screen w-full overflow-hidden transition-colors duration-300 ${isDarkMode ? 'dark bg-slate-950 text-slate-200' : 'bg-slate-50 text-slate-800'}`}>
            
            <Sidebar 
                isCollapsed={isCollapsed}
                forceSidebarOpen={forceSidebarOpen}
                setForceSidebarOpen={setForceSidebarOpen}
                isHidden={sidebarHidden}
                setIsHidden={handleSetSidebarHidden}
                hideMenuNumbers={hideMenuNumbers}
                activeModule={activeModule}
                setActiveModule={handleOpenModule}
                expandedSidebar={expandedSidebar}
                setExpandedSidebar={setExpandedSidebar}
                displayModules={displayModules}
                handleOpenModule={handleOpenModule}
                handleDrop={handleDrop}
                setDraggedItem={setDraggedItem}
                sidebarLogo={sidebarLogo}
                currentUser={currentUser}
                theme={theme}
                setTheme={setTheme}
                isDarkMode={isDarkMode}
                setIsDarkMode={setIsDarkMode}
                onLogout={logout}
                userFavorites={userFavorites}
                handleToggleFavorite={handleToggleFavorite}
            />

            <main className="flex-1 h-full relative overflow-hidden flex flex-col bg-slate-50 dark:bg-slate-950">
                
                <BarraSuperior 
                    menus={displayModules}
                    openTabs={openTabs}
                    activeMenu={activeModule?.id || activeModule}
                    setActiveMenu={(id) => {
                        const found = displayModules.find(m => m.id === id) || 
                                      (id === 'favorites' ? 'favorites' : 
                                      (id === 'ajuda-melhorias' ? { id: 'ajuda-melhorias', title: 'Ajuda e Melhorias', label: 'Ajuda e Melhorias', tabColor: '#0891b2', color: 'from-cyan-600 to-teal-500' } : null));
                        handleOpenModule(found);
                    }}
                    fecharAba={(id) => handleCloseTab({ stopPropagation: () => {} }, id)}
                    themeMode={theme}
                    proximoTema={proximoTema}
                    IconeTema={IconeTema}
                    rotuloTema={rotuloTema}
                    abrirCentralAjuda={abrirCentralAjuda}
                    interfaceZoom={interfaceZoom}
                    reduzirZoomInterface={reduzirZoomInterface}
                    aumentarZoomInterface={aumentarZoomInterface}
                    restaurarZoomInterface={restaurarZoomInterface}
                    notificacoesTopoAberto={notificacoesTopoAberto}
                    setNotificacoesTopoAberto={setNotificacoesTopoAberto}
                    orientacoesPendentesTopo={orientacoesCount}
                    sugestoesPendentesTopo={sugestoesCount}
                    notificacoesLista={userNotifications}
                    abrirNotificacao={abrirNotificacao}
                    setModalLimpezaCacheAberto={setModalLimpezaCacheAberto}
                    handleLogout={logout}
                    user={currentUser}
                    obterIniciais={obterIniciais}
                    hideMenuNumbers={hideMenuNumbers}
                    toggleHideMenuNumbers={toggleHideMenuNumbers}
                />

                {/* Área de Visualização das Telas */}
                {!activeModule && (
                    <Dashboard 
                        isDarkMode={isDarkMode}
                        homeZoom={homeZoom}
                        handleHomeZoomChange={handleHomeZoomChange}
                        displayModules={displayModules}
                        userFavorites={userFavorites}
                        handleToggleFavorite={handleToggleFavorite}
                        handleCycleCardSize={handleCycleCardSize}
                        handleOpenModule={handleOpenModule}
                        setDraggedItem={setDraggedItem}
                        handleDrop={handleDrop}
                    />
                )}

                {activeModule === 'favorites' && (
                    <FavoritesScreen 
                        isDarkMode={isDarkMode}
                        favZoom={favZoom}
                        handleFavZoomChange={handleFavZoomChange}
                        isDefaultFavorites={isDefaultFavorites}
                        setIsDefaultFavorites={setIsDefaultFavorites}
                        currentUser={currentUser}
                        showHiddenFavorites={showHiddenFavorites}
                        setShowHiddenFavorites={setShowHiddenFavorites}
                        displayFavorites={displayFavorites}
                        hubHiddenFavorites={hubHiddenFavorites}
                        handleHideFavoriteFromHub={handleHideFavoriteFromHub}
                        handleCycleCardSize={handleCycleCardSize}
                        handleOpenModule={handleOpenModule}
                        setDraggedItem={setDraggedItem}
                        handleDrop={handleDrop}
                    />
                )}

                {/* Exibição de Abas Ativas */}
                {openTabs.map(tab => {
                    const isVisible = activeModule?.id === tab.id;
                    return (
                        <div key={tab.id} className={`flex-1 w-full h-full relative bg-white dark:bg-slate-950 flex-col ${isVisible ? 'flex' : 'hidden'}`}>
                            <div className="flex-1 relative w-full h-full bg-slate-50 dark:bg-slate-950 overflow-hidden">
                                {tab.id === 'ajuda-melhorias' ? (
                                    <CentralAjuda 
                                        usuario={currentUser}
                                        fecharAba={(id) => handleCloseTab({ stopPropagation: () => {} }, id)}
                                    />
                                ) : (tab.id === '99' || tab.id === '60') ? (
                                    <div className="h-full p-4 overflow-hidden">
                                        <AdmModule 
                                            isDarkMode={isDarkMode} 
                                            authReady={authReady} 
                                            currentUser={currentUser} 
                                            displayModules={displayModules} 
                                            logos={logos} 
                                            saveSetting={saveSetting} 
                                        />
                                    </div>
                                ) : tab.url ? (
                                    <iframe src={tab.url} title={`Módulo ${tab.title}`} className="w-full h-full border-0 absolute inset-0" allowFullScreen />
                                ) : (
                                    <div className="flex flex-col items-center justify-center h-full w-full p-8 text-center animate-fade-in">
                                        <div className={`w-24 h-24 rounded-3xl flex items-center justify-center mb-6 shadow-xl bg-gradient-to-br ${tab.color} text-white`}>
                                            {React.createElement(tab.icon || getDynamicIcon(tab.title), { size: 48 })}
                                        </div>
                                        <h2 className={`text-3xl font-black mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Módulo {tab.title}</h2>
                                        <p className={`text-sm max-w-md ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Este sistema ainda não foi vinculado a uma URL externa ou não possui implementação nativa finalizada.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </main>

            {/* MODAL DE LIMPEZA DE CACHE */}
            {modalLimpezaCacheAberto && (
                <div className="facil-modal-overlay">
                    <div className="facil-cache-modal">
                        <div className="facil-cache-modal-header">
                            <Database className="text-amber-500 shrink-0" size={24} />
                            <div>
                                <h3 className="text-slate-800 dark:text-slate-100">Limpeza seletiva do cache</h3>
                                <p className="text-slate-500 dark:text-slate-400">
                                    Escolha somente os módulos que deseja limpar.
                                    Configurações gravadas no Firebase não serão apagadas.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setModalLimpezaCacheAberto(false)}
                                className="w-8 h-8 rounded-full border border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-500 hover:text-slate-800 transition-colors ml-auto"
                            >
                                <X size={15} />
                            </button>
                        </div>

                        <div className="facil-cache-modal-body">
                            {CATEGORIAS_CACHE.map(categoria => (
                                <label key={categoria.id} className="flex items-center gap-3 p-3.5 border border-slate-200 dark:border-slate-800 rounded-xl cursor-pointer hover:bg-slate-50/60 dark:hover:bg-slate-800/60 transition-colors">
                                    <input
                                        type="checkbox"
                                        checked={categoriasCacheSelecionadas.has(categoria.id)}
                                        onChange={() => alternarCategoriaCache(categoria.id)}
                                        className="w-4.5 h-4.5 text-cyan-600 border-slate-300 rounded focus:ring-cyan-500"
                                    />
                                    <div className="flex flex-col">
                                        <span className="font-bold text-xs text-slate-800 dark:text-slate-100">{categoria.label}</span>
                                        {categoria.desc && <span className="text-[10px] text-slate-400 mt-0.5">{categoria.desc}</span>}
                                    </div>
                                </label>
                            ))}
                        </div>

                        <div className="facil-cache-modal-footer">
                            <button
                                type="button"
                                onClick={() => setModalLimpezaCacheAberto(false)}
                                className="px-4 py-2 rounded-xl text-xs font-black bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:text-slate-850 transition-all"
                            >
                                Cancelar
                            </button>

                            <button
                                type="button"
                                onClick={confirmarLimpezaCache}
                                disabled={
                                    limpandoCacheCategorias ||
                                    categoriasCacheSelecionadas.size === 0
                                }
                                className="px-4 py-2 rounded-xl text-xs font-black bg-[#d7831f] hover:bg-[#b46513] text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            >
                                {limpandoCacheCategorias
                                    ? 'Limpando...'
                                    : 'Limpar selecionados'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style jsx="true">{`
                .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 10px; }
                .dark .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #475569; }
                .animate-fade-in { animation: fadeIn 0.3s ease-in-out; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
}

export default function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <AnonymizeProvider>
                    <HubApp />
                </AnonymizeProvider>
            </AuthProvider>
        </ThemeProvider>
    );
}